import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  TextField as MUITextField,
} from '@mui/material'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { TextField } from 'formik-material-ui'
import { cnpjMask, telefoneMask } from '../../../utils/masks'
import { MdSettings } from 'react-icons/md'
import { useEffect, useState } from 'react'
import SequenciaDialog from './SequenciaDialog'
import AutoComplete from '../../../components/AutoComplete/AutoComplete'
import { metodoPrograma } from '../../../components/api'
import { CODIGO, esquema, preparaDados, valoresIniciais } from './formulario'

const grupos = [
  {
    titulo: 'Dados da empresa',
    campos: [
      {
        name: 'dsRazaoSocial',
        label: 'Razão social',
        maxLength: 255,
        required: true,
        md: 8,
      },
      { name: 'nrCnpj', label: 'CNPJ', maxLength: 18, required: true, md: 4 },
      {
        name: 'idCidadeIbge',
        label: 'Código IBGE da cidade',
        maxLength: 10,
        required: true,
        md: 4,
      },
      { name: 'dsEmail', label: 'E-mail', maxLength: 100, md: 5 },
      { name: 'nrTelefone', label: 'Telefone', maxLength: 20, md: 3 },
    ],
  },
  {
    titulo: 'Certificado digital',
    campos: [
      {
        name: 'dsPathCertificado',
        label: 'Nome do certificado',
        maxLength: 20,
        required: true,
        md: 6,
      },
      {
        name: 'dsSenha',
        label: 'Senha do certificado',
        maxLength: 120,
        type: 'password',
        md: 6,
      },
    ],
  },
  {
    titulo: 'Numeração das notas fiscais',
    campos: [
      { name: 'nrSerie', label: 'Número de Série', type: 'number', md: 4 },
      { name: 'nmSequence', label: 'Nome da sequência', maxLength: 50, md: 8 },
    ],
  },
]

function ConsultaProximaDps({ onChange, onStatus }) {
  const { values } = useFormikContext()
  useEffect(() => {
    const nome = values.nmSequence?.trim()
    let ativa = true
    if (!nome) {
      onChange(null)
      onStatus('Informe o nome da sequência')
      return () => {
        ativa = false
      }
    }
    onChange(null)
    onStatus('Consultando sequência...')
    const timer = setTimeout(async () => {
      try {
        const resposta = await metodoPrograma(
          'consultaSequencia',
          { nmSequence: nome },
          CODIGO,
        )
        if (!ativa) return
        if (resposta.status && resposta.existe) {
          onChange(resposta.proximaDps)
          onStatus('')
        } else if (resposta.status)
          onStatus(
            'A sequência ainda não existe. Use a engrenagem para criá-la.',
          )
        else
          onStatus(resposta.dsErro || 'Não foi possível consultar a sequência.')
      } catch {
        if (ativa) onStatus('Não foi possível consultar a sequência.')
      }
    }, 400)
    return () => {
      ativa = false
      clearTimeout(timer)
    }
  }, [values.nmSequence, onChange, onStatus])
  return null
}

export default function Edicao({ linha = [], close, onSalvo }) {
  const alteracao = !!linha[0]
  const [sequenciaAberta, setSequenciaAberta] = useState(false)
  const [proximaDps, setProximaDps] = useState(null)
  const [statusSequencia, setStatusSequencia] = useState('')
  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Typography variant="h6" component="h1" sx={{ mb: 3 }}>
        {alteracao
          ? 'Alterar empresa de nota fiscal'
          : 'Incluir empresa de nota fiscal'}
      </Typography>
      <Formik
        initialValues={valoresIniciais(linha)}
        enableReinitialize
        validationSchema={esquema}
        onSubmit={async (valores, { setStatus }) => {
          setStatus(null)
          try {
            const resposta = await metodoPrograma(
              'salva',
              preparaDados(valores),
              CODIGO,
            )
            if (resposta.status) {
              onSalvo()
              close()
            } else {
              setStatus(resposta.dsErro || 'Não foi possível salvar a empresa.')
            }
          } catch {
            setStatus(
              'Não foi possível comunicar com o servidor. Tente novamente.',
            )
          }
        }}
      >
        {({
          isSubmitting,
          status,
          values,
          errors,
          touched,
          setValues,
          setFieldTouched,
        }) => (
          <Form>
            <ConsultaProximaDps
              onChange={setProximaDps}
              onStatus={setStatusSequencia}
            />
            <Grid container spacing={2}>
              {grupos.map(({ titulo, campos }) => (
                <Grid item xs={12} key={titulo}>
                  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography
                      variant="subtitle1"
                      component="h2"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {titulo}
                    </Typography>
                    <Grid container spacing={3}>
                      {campos.map(
                        ({ name, label, maxLength, required, md, type }) => (
                          <Grid item xs={12} md={md} key={name}>
                            {name === 'nmSequence' ? (
                              <Grid container spacing={1} alignItems="center">
                                <Grid item xs={7}>
                                  <Field
                                    component={TextField}
                                    name={name}
                                    label={label}
                                    inputProps={{ maxLength }}
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                  />
                                </Grid>
                                <Grid item xs={4}>
                                  <MUITextField
                                    label="Próxima DPS"
                                    value={proximaDps ?? ''}
                                    InputProps={{ readOnly: true }}
                                    helperText={statusSequencia}
                                    fullWidth
                                    size="small"
                                  />
                                </Grid>
                                <Grid item xs={1}>
                                  <Tooltip title="Consultar ou ajustar sequência">
                                    <span>
                                      <IconButton
                                        aria-label="Configurar sequência"
                                        disabled={!values.nmSequence?.trim()}
                                        onClick={() => setSequenciaAberta(true)}
                                      >
                                        <MdSettings />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Grid>
                              </Grid>
                            ) : name === 'idCidadeIbge' ? (
                              <AutoComplete
                                label="Cidade (código IBGE e nome)"
                                metodo="buscaCidadeIbge"
                                value={values.acCidadeIbge}
                                disabled={isSubmitting}
                                fullWidth
                                size="small"
                                isOptionEqualToValue={(option, value) =>
                                  option?.[0]?.[0] === value?.[0]?.[0]
                                }
                                onChange={(_, cidade) =>
                                  setValues((atual) => ({
                                    ...atual,
                                    acCidadeIbge: cidade,
                                    idCidadeIbge: cidade?.[0]?.[0] || '',
                                  }))
                                }
                                onBlur={() =>
                                  setFieldTouched('idCidadeIbge', true)
                                }
                                textFieldProps={{
                                  required: true,
                                  error:
                                    touched.idCidadeIbge &&
                                    !!errors.idCidadeIbge,
                                  helperText:
                                    touched.idCidadeIbge && errors.idCidadeIbge,
                                  placeholder:
                                    'Digite o código ou o nome da cidade',
                                }}
                              />
                            ) : (
                              <Field
                                component={TextField}
                                name={name}
                                label={label}
                                type={type || 'text'}
                                InputProps={
                                  name === 'nrCnpj'
                                    ? cnpjMask
                                    : name === 'nrTelefone'
                                      ? telefoneMask
                                      : undefined
                                }
                                autoComplete={
                                  name === 'dsSenha'
                                    ? 'new-password'
                                    : undefined
                                }
                                required={
                                  name === 'dsSenha' ? !alteracao : required
                                }
                                inputProps={
                                  type === 'number'
                                    ? {
                                        step: 1,
                                        min: -2147483648,
                                        max: 2147483647,
                                      }
                                    : { maxLength }
                                }
                                helperText={
                                  name === 'dsSenha' && alteracao
                                    ? 'Deixe em branco para manter a senha atual.'
                                    : undefined
                                }
                                fullWidth
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              ))}
              {status && (
                <Grid item xs={12}>
                  <Alert severity="error">{status}</Alert>
                </Grid>
              )}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
              >
                <Button onClick={close} disabled={isSubmitting}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </Grid>
            </Grid>
            <SequenciaDialog
              open={sequenciaAberta}
              nome={values.nmSequence?.trim() || ''}
              onClose={() => setSequenciaAberta(false)}
              onChange={setProximaDps}
            />
          </Form>
        )}
      </Formik>
    </Box>
  )
}
